<template>
  <div class="container">
    <div class="paywall">
      <h1 class="page-title" id="page-title-for-sr">{{ $t('paywall.header') }}</h1>
      <div class="columns" :class="{confirmation: showPaywayNotAvailableInfo}">
        <section class="sidebar-column" id="payment-section-summary" aria-labelledby="section-summary-heading" v-if="canShowDesktopOrderDetails">
          <order-details-side :transaction="transaction"
                              :visibility="visibilityOptions"
                              :is-blik="isBlikAuto"
                              :is-paywall="true"
                              :show-button="true"/>
        </section>
        <section class="main-column">
          <div class="content confirmation-wrapper" v-if="showPaywayNotAvailableInfo">
            <info-payway-not-available/>
          </div>
          <div class="content" v-if="!showPaywayNotAvailableInfo">
            <div v-if="paywayGroups">
              <div class="payway-method-switch" v-if="canShowPaywayMethodSwitch()">
                <div role="radio" tabindex="0" name="payway-method" class="payway-method payway-single"
                     :class="{'active': isPaywayMethodSingle()}"
                     :aria-checked="isPaywayMethodSingle()"
                     @click="setPaywayMethodSingle()"
                     @keydown.13.stop="setPaywayMethodSingle()"
                     @keydown.32.prevent
                     @keyup.32="setPaywayMethodSingle()">
                  {{ $t('paywall.paywaySwitch.methodShortSingle') }}
                </div>
                <div role="radio" tabindex="0" name="payway-method" class="payway-method payway-auto"
                     :class="{'active': isPaywayMethodAuto()}"
                     :aria-checked="isPaywayMethodAuto()"
                     @click="setPaywayMethodAuto()"
                     @keydown.13.stop="setPaywayMethodAuto()"
                     @keydown.32.prevent
                     @keyup.32="setPaywayMethodAuto()">
                  {{ $t('paywall.paywaySwitch.methodShortAuto') }}
                </div>
              </div>
              <ul id="payway-radio-group">
                <template v-for="(paywayGroup, index) in paywayGroups">
                  <li v-if="canShowPaymentGroup(paywayGroup.type)" :key="index">
                    <payment-payway-group :group-id="'payway-radio-' + paywayGroup.type" :group="paywayGroup"/>
                  </li>
                </template>
                <li v-if="isPaymentBoostActive && isPaymentBoostGroupsHidden">
                  <div class="payment-type-container">
                    <div class="payment-type-box payment-boost-more">
                      <button class="btn btn-plain" @click="showPaymentBoostHiddenGroups">
                        <span>{{ $t('paywall.paymentBoost.button') }}</span>
                      </button>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div class="back-link" v-if="visibilityOptions && visibilityOptions.showReturnToShopButton">
            <button type="button"
                    class="btn btn-plain link-back-to-shop"
                    :title="$t('orderDetails.linkBackTitle')"
                    v-if="isPaywallView"
                    @click="sendPaymentReject">
              {{ $t('orderDetails.linkBack') }}
            </button>
            <button type="button"
                    class="btn btn-plain link-back-to-shop"
                    :title="$t('orderDetails.linkBack')"
                    v-if="!isPaywallView"
                    @click="goBackToShop(returnUrl)">
              {{ $t('orderDetails.linkBack') }}
            </button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import PaymentPaywayGroup from '@/base/components/Payment/Paywall/PaywayGroup'
import OrderDetailsSide from '@/base/components/OrderDetails/Side'
import { mapGetters, mapState, mapMutations, mapActions } from 'vuex'
import { MatomoClass } from '@/base/class/matomo.class'
import { MATOMO_EVENT_CATEGORY, MATOMO_EVENT_TYPE, MATOMO_PRODUCT_SKU, MATOMO_VIEW_ACTION } from '@/base/const/matomo.const'
import { DateClass } from '@/base/class/date.class'
import { MobileAppClass } from '@/base/class/mobileApp.class'
import { MOBILE_STATUS } from '@/base/const/mobileEvents.const'
import { PAYWALL_MODE, PAYWALL_PAYWAY_METHOD } from '@/base/const/paywall.const'
import { ToolsClass } from '@/base/class/tools.class'
import InfoPaywayNotAvailable from '@/base/components/Payment/Info/PaywayNotAvailable'
import { AUTO_ID_LIST, PAYWAY_GROUPS_TYPES } from '@/base/const/payway.const'
import { Raven } from 'vue-raven'
import { ROUTING } from '@/base/const/routing.const'
import { PaymentBoost } from '@/base/class/paymentBoost.class'

export default {
  name: 'PaymentPaywall',
  components: {
    InfoPaywayNotAvailable,
    PaymentPaywayGroup,
    OrderDetailsSide
  },
  data () {
    return {
      showPaywayNotAvailableInfo: false
    }
  },
  computed: {
    ...mapState({
      transaction: state => state.payment.paymentData.transaction,
      visibilityOptions: state => state.payment.paymentData.visibilityOptions,
      click2PayData: state => state.payment.paymentData.click2PayData,
      serviceType: state => state.payment.paymentData.serviceType,
      serviceId: state => state.payment.paymentData.serviceID,
      transRemoteID: state => state.payment.paymentData.transRemoteID,
      isPaywallModeChosenPayway: state => state.payment.paywallMode === PAYWALL_MODE.chosenPayway,
      isPaywallView: state => state.payment.paywallView,
      chosenPaywayId: state => state.payment.paymentData.chosenPaywayId,
      paywayMethod: state => state.payment.paywayMethod,
      returnUrl: state => state.payment.paymentData.returnUrl,
      customerEmail: state => state.payment.form.customerEmail,
      selectedPaywayGroupType: state => state.payment.selectedPaywayGroupType,
      language: state => state.lang.current,
      isPaymentBoostActive: state => state.payment.isPaymentBoostActive,
      isPaymentBoostGroupsHidden: state => state.payment.isPaymentBoostGroupsHidden
    }),
    ...mapGetters({
      paywayGroups: 'getPaymentGroupList',
      isDefaultLangActive: 'isDefaultLangActive',
      isPaymentCardAvailable: 'isPaymentCardAvailable'
    }),
    isPaymentMethodAutoAvailable: function () {
      return !!this.paywayGroups.find(group => [PAYWAY_GROUPS_TYPES.auto, PAYWAY_GROUPS_TYPES.blikAuto].includes(group.type))
    },
    isPaymentMethodSingleAvailable: function () {
      return !!this.paywayGroups.find(group => ![PAYWAY_GROUPS_TYPES.auto, PAYWAY_GROUPS_TYPES.blikAuto].includes(group.type))
    },
    canShowDesktopOrderDetails: function () {
      return !this.$VUE_HIDE_TRANSACTION_INFO
    },
    isBlikAuto: function () {
      return this.selectedPaywayGroupType === PAYWAY_GROUPS_TYPES.blikAuto
    }
  },
  methods: {
    ...mapMutations([
      'SET_TIMER_PAYWALL_VIEW',
      'SET_ERROR_DATA',
      'SET_PAYMENT_PAYWALL_VIEW',
      'SET_PAYWAY_METHOD',
      'SET_PAYMENT_FORM_PAYWAY_ID',
      'SET_PAYMENT_FORM_REGULATION_ID',
      'SET_PAYMENT_FORM_REGULATION_APPROVED',
      'SET_PAYMENT_BOOST_GROUPS_HIDDEN'
    ]),
    ...mapActions([
      'setPaymentGroupSelected',
      'setPaymentActivePayway',
      'sendPaymentRejectTrx',
      'setC2PInitData'
    ]),
    goBackToShop (returnUrl) {
      if (ToolsClass.isMobileAppConnection()) {
        MobileAppClass.callMobileTransactionFinish()
        return
      }
      ToolsClass.goToExternalUrl(returnUrl)
    },
    sendPaymentReject () {
      this.sendPaymentRejectTrx()
        .then(returnUrl => {
          ToolsClass.goToExternalUrl(returnUrl)
        })
        .catch(error => {
          MobileAppClass.callMobileTransactionFinish()
          Raven.captureMessage('sendPaymentReject Method response error', {
            extra: error
          })
        })
    },
    canShowPaywayMethodSwitch () {
      return !this.isPaywallModeChosenPayway && this.isPaymentMethodAutoAvailable && this.isPaymentMethodSingleAvailable
    },
    canShowPaymentGroup (paymentGroupType) {
      if (this.isPaymentBoostActive && this.isPaymentBoostGroupsHidden && [PAYWAY_GROUPS_TYPES.wallet, PAYWAY_GROUPS_TYPES.card, PAYWAY_GROUPS_TYPES.visaMobile, PAYWAY_GROUPS_TYPES.promo].includes(paymentGroupType)) {
        return false
      }
      if (this.isPaywayMethodSingle()) {
        return ![PAYWAY_GROUPS_TYPES.auto, PAYWAY_GROUPS_TYPES.blikAuto].includes(paymentGroupType)
      } else {
        return [PAYWAY_GROUPS_TYPES.auto, PAYWAY_GROUPS_TYPES.blikAuto].includes(paymentGroupType)
      }
    },
    isPaywayMethodSingle () {
      return this.paywayMethod === PAYWALL_PAYWAY_METHOD.single
    },
    setPaywayMethodSingle () {
      if (this.isPaywayMethodSingle()) {
        return
      }
      this.SET_PAYWAY_METHOD(PAYWALL_PAYWAY_METHOD.single)
      this.SET_PAYMENT_FORM_PAYWAY_ID(null)
      this.SET_PAYMENT_FORM_REGULATION_ID(null)
      this.SET_PAYMENT_FORM_REGULATION_APPROVED(false)
    },
    isPaywayMethodAuto () {
      return [PAYWAY_GROUPS_TYPES.auto, PAYWAY_GROUPS_TYPES.blikAuto].includes(this.paywayMethod)
    },
    goToPaywayError () {
      this.SET_PAYMENT_PAYWALL_VIEW(false)
      this.$router.push(ROUTING.paywayError)
    },
    setPaywayMethodAuto () {
      if (this.isPaywayMethodAuto()) {
        return
      }
      this.SET_PAYWAY_METHOD(PAYWALL_PAYWAY_METHOD.auto)
      this.activateAutoGroup()
      if (!this.paywayGroups || !this.paywayGroups.length) {
        this.goToPaywayError()
      }
    },
    setPaywayGroup (group) {
      this.setPaymentGroupSelected(group)
    },
    sendEventEcommerceView () {
      if (!this.transaction || !MatomoClass.isMatomoLoaded()) {
        setTimeout(this.sendEventEcommerceView, 100)
        return
      }
      const event = [
        MATOMO_EVENT_TYPE.setEcommerceView,
        MATOMO_PRODUCT_SKU[this.serviceType],
        this.serviceType,
        '',
        this.transaction.payAmount
      ]
      MatomoClass.sendEvent(event)
      MatomoClass.sendTrackPageView()
      setTimeout(this.sendEventEcommerceAddItem, 2000)
    },
    sendEventEcommerceAddItem () {
      const event = [
        MATOMO_EVENT_TYPE.addEcommerceItem,
        MATOMO_PRODUCT_SKU[this.serviceType],
        this.serviceType,
        'Płatność',
        this.transaction.payAmount,
        1
      ]
      MatomoClass.sendEvent(event)
    },
    activateAutoGroup () {
      const autoGroup = this.paywayGroups.filter(group => [PAYWAY_GROUPS_TYPES.auto, PAYWAY_GROUPS_TYPES.blikAuto].includes(group?.type))
      if (!autoGroup.length) {
        this.goToPaywayError()
        return
      }
      if (autoGroup.length === 1) {
        const selectedAutoGroup = autoGroup[0]
        this.setPaywayGroup(selectedAutoGroup)
        if (selectedAutoGroup.payways.length === 1) {
          this.setPaymentActivePayway(selectedAutoGroup.payways[0])
        } else {
          this.setPaymentActivePayway(null)
        }
      }
    },
    showPaymentBoostHiddenGroups () {
      this.SET_PAYMENT_BOOST_GROUPS_HIDDEN(false)
      if (PaymentBoost.canSendCardDepreciationMechanismShowGroupsEvent(this.transRemoteID)) {
        PaymentBoost.sendCardDepreciationMechanismShowGroupsEvent(this.serviceId, this.transRemoteID)
      }
    }
  },
  mounted () {
    if (window.yourcx && this.$VUE_YOURCX_CONFIG.product.audit.active && this.isDefaultLangActive) {
      window.yourcx('survey', this.$VUE_YOURCX_CONFIG.product.audit.surveyId, { product: 'audit' })
    }

    MatomoClass.sendEvent([MATOMO_EVENT_TYPE.trackEvent, MATOMO_EVENT_CATEGORY.view, MATOMO_VIEW_ACTION.paywall])
    this.sendEventEcommerceView()
    this.SET_TIMER_PAYWALL_VIEW(DateClass.getCurrentTimestamp())
    MobileAppClass.postMessageSuccess(MOBILE_STATUS().paywaylistLoaded)

    if (this.isPaymentBoostActive) {
      if (PaymentBoost.canSendCardDepreciationMechanismActiveEvent(this.transRemoteID)) {
        PaymentBoost.sendCardDepreciationMechanismActiveEvent(this.serviceId, this.transRemoteID)
      }
    }

    // inicjalizacja Click2Pay
    if (this.isPaymentCardAvailable && this.click2PayData) {
      this.setC2PInitData(this.click2PayData)
        .catch(error => {
          if (ToolsClass.isDebugEnabled()) {
            console.log('inicjalizacja Click2Pay error', error)
          }
        })
    }

    // start z wybranym kanałem walletowym, który jest niedostępny
    if (this.isPaywallModeChosenPayway && !ToolsClass.isWalletPaywayAvailable(this.chosenPaywayId)) {
      if (ToolsClass.isDebugEnabled()) {
        console.log('start z wybranym kanałem walletowym, który jest niedostępny')
      }
      this.SET_PAYMENT_PAYWALL_VIEW(false)
      this.showPaywayNotAvailableInfo = true
      return
    }

    // start z wybranym kanałem płatności automatycznej
    if (this.isPaywallModeChosenPayway && AUTO_ID_LIST.includes(this.chosenPaywayId)) {
      if (ToolsClass.isDebugEnabled()) {
        console.log('start z wybranym kanałem płatności automatycznej')
      }
      this.setPaywayMethodAuto()
      return
    }

    // start tylko z grupami płatności automatycznych
    if (!this.isPaywallModeChosenPayway && this.isPaymentMethodAutoAvailable && !this.isPaymentMethodSingleAvailable) {
      if (ToolsClass.isDebugEnabled()) {
        console.log('start tylko z kanałami płatności automatycznej')
      }
      this.setPaywayMethodAuto()
      return
    }

    // normalny start, ale dostępna tylko 1 grupa płatności (start z wybranym kanałem płatności)
    if (this.paywayGroups.length === 1 && this.paywayGroups[0].active) {
      if (ToolsClass.isDebugEnabled()) {
        console.log('normalny start, ale dostępna tylko 1 grupa płatności (start z wybranym kanałem płatności)')
      }
      if (this.isPaymentMethodAutoAvailable) {
        this.setPaywayMethodAuto()
      } else {
        this.setPaywayGroup(this.paywayGroups[0])
      }
    }

    // brak grup płatności
    if (this.paywayGroups.length === 0) {
      if (ToolsClass.isDebugEnabled()) {
        console.log('brak grup płatności')
      }
      this.goToPaywayError()
    }

    if (ToolsClass.isDebugEnabled()) {
      console.log('normalny start paywalla')
    }
  },
  updated () {
    if (this.isPaywayMethodAuto() && this.paywayGroups.length === 1) {
      this.activateAutoGroup()
    }
  }
}
</script>

<style lang="scss" scoped>
@import "./src/base/assets/scss/components/Paywall.scss";
</style>
